import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { Document, DocumentVersion, ErrorAlert } from '@xdocs/schemas';
import { useDocument } from '../documents/index.js';
import { useWs } from '../../ws/WsProvider.js';

export interface UseReadDocumentResult {
  document: Document | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
  version: DocumentVersion | undefined;
  isVersionLoading: boolean;
  isVersionError: boolean;
  versionError: ErrorAlert | null;
  activeVersionId: string | null;
  setActiveVersionId: (versionId: string) => void;
  /** F66 AC: "shared doc updated mid-read -> 'new version available' offer" —
   * true when the document's live `currentVersionId` (kept fresh by
   * `useDocument`'s `document:{documentId}` invalidation) has moved past
   * the version currently being read. */
  hasNewerVersion: boolean;
  switchToLatestVersion: () => void;
}

/**
 * F66 reader data: the document (read-only, ACL-gated server-side —
 * `document.get` already resolves owner/staffOverride/share-grant/public/
 * link per CODE_GUIDELINES §15.2 ACL order, `FORBIDDEN`/`NOT_FOUND` surfaced
 * as-is) plus the active `document_versions` row (`version.get`), with an
 * explicit version/language switch surface for F17 AC "per-VERSION resume
 * position when switching version/language".
 */
export function useReadDocument(documentId: string): UseReadDocumentResult {
  const { wsClient, connectionState } = useWs();
  const connected = connectionState === 'connected';
  const { document, isLoading, isError, error, refetch } = useDocument(documentId);

  const [activeVersionId, setActiveVersionIdState] = useState<string | null>(null);

  useEffect(() => {
    if (activeVersionId === null && document?.currentVersionId) {
      setActiveVersionIdState(document.currentVersionId);
    }
  }, [document?.currentVersionId, activeVersionId]);

  const versionEnabled = connected && activeVersionId !== null;
  const versionQuery = useQuery<DocumentVersion, ErrorAlert>({
    queryKey: ['document', documentId, 'version', activeVersionId],
    queryFn: () => requestTyped(wsClient, 'version.get', { documentId, versionId: activeVersionId as string }),
    enabled: versionEnabled,
  });

  const setActiveVersionId = useCallback((versionId: string) => setActiveVersionIdState(versionId), []);
  const switchToLatestVersion = useCallback(() => {
    if (document?.currentVersionId) setActiveVersionIdState(document.currentVersionId);
  }, [document?.currentVersionId]);

  const hasNewerVersion = Boolean(
    document?.currentVersionId && activeVersionId && document.currentVersionId !== activeVersionId,
  );

  return {
    document,
    isLoading,
    isError,
    error,
    refetch,
    version: versionQuery.data,
    isVersionLoading: versionEnabled ? versionQuery.isLoading : activeVersionId === null,
    isVersionError: versionQuery.isError,
    versionError: versionQuery.isError ? versionQuery.error : null,
    activeVersionId,
    setActiveVersionId,
    hasNewerVersion,
    switchToLatestVersion,
  };
}
